import { useEffect } from 'react'

import { z } from 'zod'

import { Loadable, fromLoadable, mapLoadable } from 'utils/loadable'

/**
 * Hook for caching Loadable data to LocalStorage. Returns cached data while the
 * 'live' data is still loading, and updates the cache any time the live data
 * changes.
 */
export const useCachedData = <T>(
  key: string,
  data: Loadable<T>,
  /**
   * Function that condenses `data` into some smaller subset. Useful if the size
   * of the raw `data` object is too large to fit in LocalStorage.
   */
  condense?: (data: T) => T,
): Loadable<T> => {
  const lockKey = `useCachedData:${key}:updated_at`
  const dataKey = `useCachedData:${key}:data`
  const condensed = mapLoadable(data, condense ?? ((data: T) => data))
  const serialized = fromLoadable(mapLoadable(condensed, JSON.stringify))

  useEffect(() => {
    if (typeof serialized === 'undefined') return
    // Add a small amount of jitter to reduce the chance of synchronised writes
    const c = setTimeout(
      () => checkAndSet(lockKey, dataKey, serialized),
      Math.random() * 10,
    )
    return () => clearTimeout(c)
  }, [dataKey, lockKey, serialized])

  if (data.loading) {
    const cache = localStorage.getItem(dataKey)
    if (cache === null || cache === '' || cache === 'null') {
      return { loading: true }
    }
    return { loading: false, data: JSON.parse(cache) }
  }

  return data
}

/** Get last write time for previously-written data */
const getCacheUpdatedAt = (key: string): Date | undefined => {
  const updatedAtRaw = localStorage.getItem(key)
  if (updatedAtRaw === null) return undefined
  const res = z.string().datetime().safeParse(updatedAtRaw)
  return res.success ? new Date(res.data) : undefined
}

/**
 * Multiple tabs will be using the cache, and we don't want them all hammering
 * LocalStorage with the same data when they all get the same usePollApi data at
 * the same instant. So, if something has already saved data in the last 15
 * seconds, we don't need to repeat it.
 */
const checkAndSet = (
  lockKey: string,
  dataKey: string,
  serializedData: string,
) => {
  const lock = getCacheUpdatedAt(lockKey)
  if (!lock || lock.getTime() < Date.now() - 15000) {
    localStorage.setItem(dataKey, serializedData)
    localStorage.setItem(lockKey, new Date().toISOString())
  }
}
